import { useEffect, useRef, useState } from 'react';

import {
  Box,
  Button as MuiButton,
  Card,
  Chip,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { radius } from '@karehero/llama/theme/radius';

import BottomSheet from '@/designs-dev/device/BottomSheet';
import StatusBar from '@/designs-dev/device/StatusBar';
import ScreenRoot from '@/components/wrapped/ScreenRoot';
import KeniOrb from '@/components/wrapped/KeniOrb';
import ChatTyping from '@/components/wrapped/ChatTyping';
import ChatBubble from '@/karehero-app/tell-us-once/intake/ChatBubble';
import VoiceNote from '@/karehero-app/tell-us-once/intake/VoiceNote';
import Attachment from '@/karehero-app/tell-us-once/intake/Attachment';
import ClaimActionRow from '@/james-playground/aa-family-claim/ClaimActionRow';
import ConflictChooser from '@/james-playground/aa-family-claim/ConflictChooser';
import { ClaimBar, ScreenFloor } from '@/james-playground/aa-family-claim/ClaimScreen';
import AskCard from '@/james-playground/aa-family-claim/claim/cards/AskCard';
import BatchCheckCard from '@/james-playground/aa-family-claim/claim/cards/BatchCheckCard';
import {
  AREAS,
  ASK,
  DOCUMENTS,
  OWNER,
  VOICE_PROMPTS,
  say,
  type AreaId,
  type Ask,
  type DocumentKind,
} from '@/james-playground/aa-family-claim/claim/asks';
import {
  finishRound,
  hearVoice,
  readDocument,
  readTyped,
  voiceOf,
  type ClaimState,
} from '@/james-playground/aa-family-claim/claim/claim';
import { ROUND } from '@/james-playground/aa-family-claim/claim/claim.content';
import { statusOf, type Step } from '@/james-playground/aa-family-claim/claim/engine';
import {
  SOURCE_LABEL,
  answer,
  answerAll,
  settle,
  skip,
  text,
  type Facts,
  type Value,
} from '@/james-playground/aa-family-claim/claim/facts';
import { labelOf, valueText } from '@/james-playground/aa-family-claim/claim/labels';
import { entry, isAsked } from '@/james-playground/aa-family-claim/claim/registry';
import { SCENARIO, type Recording as Script } from '@/james-playground/aa-family-claim/claim/scenarios';

const SCREEN_PX = 3;

type FoundSource = 'voice' | 'typed' | DocumentKind;

type Turn =
  | { id: number; kind: 'keni'; text: string }
  | { id: number; kind: 'user'; text: string }
  | { id: number; kind: 'voice'; seconds: number; words: string }
  | { id: number; kind: 'attachment'; name: string }
  | { id: number; kind: 'have'; ids: string[] }
  | { id: number; kind: 'found'; source: FoundSource; ids: string[] };

type Phase = 'capture' | 'document' | 'batch' | 'ask' | 'done';

export type Recording =
  | { kind: 'idle' }
  /** `hold` freezes it mid-recording, for a flows page that shows one frame. */
  | { kind: 'recording'; elapsed: number; hold?: boolean }
  | { kind: 'draft' };

const sendButton = {
  width: 40,
  height: 40,
  flexShrink: 0,
  bgcolor: 'custom.coral.600',
  color: 'common.white',
  '&:hover': { bgcolor: 'custom.coral.700' },
  '&.Mui-disabled': { bgcolor: 'custom.neutral.200', color: 'common.white' },
} as const;

/** How long the demo takes to play a recording, so a room is not kept waiting. */
const playbackMs = (script: Script) => Math.min(13000, script.seconds * 260);

/** What the orb has heard so far, line by line against the script's clock. */
const heardSoFar = (script: Script, elapsedMs: number) => {
  const seconds = (elapsedMs / playbackMs(script)) * script.seconds;
  const lines = script.lines.map((l, i) => {
    const end = script.lines[i + 1]?.at ?? script.seconds;
    const share = Math.min(1, Math.max(0, (seconds - l.at) / Math.max(1, end - l.at)));
    const words = l.text.split(' ');
    return words.slice(0, Math.ceil(words.length * share)).join(' ');
  }).filter(Boolean);
  const lit = new Set(script.lines.filter((l) => seconds >= l.at + 1).flatMap((l) => l.lights));
  return { text: lines.join(' '), lit, seconds: Math.round(seconds) };
};

/** Entries in an ask that already have an answer, for "What we have so far". */
const answeredIn = (ask: Ask, facts: Facts) =>
  ask.entries.filter((id) => {
    const e = entry(id);
    const s = statusOf(e, facts);
    return isAsked(e) && (s === 'proposed' || s === 'confirmed' || s === 'conflict') && facts[id];
  });

/** The care areas ask about good and bad days once, after their details. */
const VARIABILITY_ASK: Partial<Record<AreaId, string>> = {
  day: 'day-variability',
  safe: 'safe-variability',
  night: 'night-variability',
};

const FoundCard = ({ source, ids, facts, claim, onRemove }: {
  source: FoundSource;
  ids: string[];
  facts: Facts;
  claim: ClaimState;
  onRemove?: (id: string) => void;
}) => {
  const v = voiceOf(claim);
  const [removed, setRemoved] = useState<string[]>([]);
  return (
    <Card sx={{ alignSelf: 'flex-start', width: '92%', p: 2 }}>
      <Stack spacing={1.5}>
        <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1}>
          <Box sx={{ display: 'flex', color: 'custom.sunflower.700' }}>
            <Icon icon={'sparkles' as never} fontSize={12} />
          </Box>
          <Typography variant='overline'>{ROUND.found[source]}</Typography>
        </Stack>
        {ids.length === 0 && <Typography variant='body2'>{ROUND.nothing}</Typography>}
        <Stack component='ul' sx={{ m: 0, p: 0, listStyle: 'none' }} spacing={1}>
          {ids.map((id) => {
            const fact = facts[id];
            const clash = fact?.state === 'conflict';
            const gone = removed.includes(id);
            return (
              <Stack key={id} component='li' sx={{ alignItems: 'flex-start', opacity: gone ? 0.5 : 1 }} direction='row' spacing={1.25}>
                <Box sx={{ display: 'flex', pt: 0.25, color: clash ? 'custom.coral.700' : 'primary.main' }}>
                  <Icon icon={(clash ? 'circle-exclamation' : 'circle-check') as never} fontSize={14} />
                </Box>
                <Stack sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant='body2'>{labelOf(id, v)}</Typography>
                  <Typography sx={{ color: 'text.secondary' }} variant='caption'>
                    {gone ? ROUND.removed : clash ? ROUND.clash : valueText(id, fact?.value)}
                  </Typography>
                </Stack>
                {onRemove && !gone && !clash && (
                  <Link
                    component='button'
                    type='button'
                    variant='caption'
                    aria-label={`${ROUND.remove}: ${labelOf(id, v)}`}
                    onClick={() => {
                      setRemoved((r) => [...r, id]);
                      onRemove(id);
                    }}
                  >
                    {ROUND.remove}
                  </Link>
                )}
              </Stack>
            );
          })}
        </Stack>
      </Stack>
    </Card>
  );
};

const HaveCard = ({ ids, facts, claim }: { ids: string[]; facts: Facts; claim: ClaimState }) => {
  const v = voiceOf(claim);
  return (
    <Card sx={{ alignSelf: 'flex-start', width: '92%', p: 2, bgcolor: 'custom.neutral.50' }}>
      <Stack spacing={1}>
        <Typography variant='overline'>{ROUND.have}</Typography>
        {ids.map((id) => (
          <Stack key={id} spacing={0}>
            <Typography variant='body2'>
              {labelOf(id, v)}: {valueText(id, facts[id]?.value)}
            </Typography>
            <Typography sx={{ color: 'text.secondary' }} variant='caption'>
              {SOURCE_LABEL[facts[id].source]}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Card>
  );
};

/**
 * One round of the gather loop (spec 3.3): it opens with what the claim has,
 * asks once, captures the answer in whichever way suits, shows what it found,
 * and returns to the hub. A round can carry follow-ups: the activities ticked
 * in the batch check, and the good-and-bad-days question after a care area.
 */
const RoundScreen = ({
  claim: initialClaim,
  step,
  onDone,
  onBack,
  initialRecording,
}: {
  claim: ClaimState;
  step: Step;
  onDone: (claim: ClaimState) => void;
  onBack: () => void;
  /** Harness-only: open mid-recording or with a recording waiting to send. */
  initialRecording?: Recording;
}) => {
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const v = voiceOf(initialClaim);
  const scenario = SCENARIO[initialClaim.scenario];
  const quicker = text(initialClaim.facts, 'specialRules') === 'Yes';
  const nextId = useRef(1);
  const turn = <T extends Omit<Turn, 'id'>>(t: T) => ({ ...t, id: nextId.current++ }) as unknown as Turn;

  const [claim, setClaim] = useState(initialClaim);
  const [added, setAdded] = useState<string[]>([]);
  // The voice prompt that a capture phase records against.
  const [promptId, setPromptId] = useState<string>(
    step.kind === 'opening' ? 'voice-opening' : step.kind === 'voice' ? step.prompt.id : '',
  );
  const [queue, setQueue] = useState<Ask[]>(step.kind === 'ask' ? [step.ask] : []);
  const [phase, setPhase] = useState<Phase>(
    step.kind === 'opening' || step.kind === 'voice'
      ? 'capture'
      : step.kind === 'document'
        ? 'document'
        : step.kind === 'batch'
          ? 'batch'
          : 'ask',
  );
  const [turns, setTurns] = useState<Turn[]>(() => {
    if (step.kind === 'opening')
      return [turn({ kind: 'keni', text: say(quicker ? ROUND.openingQuicker : step.prompt.prompt, v) })];
    if (step.kind === 'voice') return [turn({ kind: 'keni', text: say(step.prompt.prompt, v) })];
    if (step.kind === 'document') {
      const doc = DOCUMENTS.find((d) => d.kind === step.doc);
      return [turn({ kind: 'keni', text: say(doc?.prompt ?? '', v) })];
    }
    if (step.kind === 'batch') return [];
    const have = answeredIn(step.ask, initialClaim.facts);
    return [
      ...(have.length ? [turn({ kind: 'have', ids: have })] : []),
      turn({ kind: 'keni', text: say(step.ask.prompt, v) }),
    ];
  });
  const [recording, setRecording] = useState<Recording>(initialRecording ?? { kind: 'idle' });
  const [typed, setTyped] = useState('');
  const [reading, setReading] = useState(false);
  const [pending, setPending] = useState<null | ((from: ClaimState) => void)>(null);

  const script: Script | undefined = scenario.voice[promptId];
  const current = queue[0];
  const area: AreaId | undefined =
    step.kind === 'ask' ? step.ask.area : step.kind === 'voice' ? step.prompt.area : undefined;

  // ---- scrolling: stay on the latest turn while they read the latest turn ----
  const scroller = useRef<HTMLDivElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const pinned = useRef(true);
  useEffect(() => {
    const el = scroller.current;
    const inner = content.current;
    if (!el || !inner) return;
    const toBottom = () => {
      if (pinned.current) el.scrollTo({ top: el.scrollHeight, behavior: reduceMotion ? 'auto' : 'smooth' });
    };
    const observer = new ResizeObserver(toBottom);
    observer.observe(inner);
    const onScroll = () => {
      pinned.current = el.scrollHeight - el.scrollTop - el.clientHeight < 64;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    toBottom();
    return () => {
      observer.disconnect();
      el.removeEventListener('scroll', onScroll);
    };
  }, [reduceMotion]);
  useEffect(() => {
    pinned.current = true;
  }, [turns.length, phase, recording.kind]);

  // ---- the recording plays its script, then waits to be sent ----
  useEffect(() => {
    if (recording.kind !== 'recording' || recording.hold || !script) return;
    if (recording.elapsed >= playbackMs(script)) {
      setRecording({ kind: 'draft' });
      return;
    }
    const id = setTimeout(
      () => setRecording((r) => (r.kind === 'recording' ? { ...r, elapsed: r.elapsed + 100 } : r)),
      100,
    );
    return () => clearTimeout(id);
  }, [recording, script]);

  /** Anything read by AI waits for their say-so the first time. */
  const withConsent = (run: (from: ClaimState) => void) => {
    if (claim.aiConsent === undefined) setPending(() => run);
    else run(claim);
  };

  const finish = (from: ClaimState, newlyAdded: string[]) => {
    const all = [...added, ...newlyAdded];
    onDone(finishRound(from, all, area ?? (all[0] ? OWNER[all[0]]?.area : undefined)));
  };

  // ---- voice, typing and photos ----
  const sendVoice = () => {
    setRecording({ kind: 'idle' });
    const result = hearVoice(claim, promptId);
    setClaim(result.state);
    setAdded((a) => [...a, ...result.added]);
    setTurns((t) => [
      ...t,
      turn({ kind: 'voice', seconds: script?.seconds ?? 10, words: script?.lines.map((l) => l.text).join(' ') ?? '' }),
      turn({ kind: 'found', source: 'voice', ids: result.added }),
    ]);
    setPhase('done');
  };

  const sendTyped = (from: ClaimState = claim) => {
    if (!typed.trim()) return;
    const result = readTyped(from, typed, promptId || 'voice-opening');
    setClaim(result.state);
    setAdded((a) => [...a, ...result.added]);
    setTurns((t) => [...t, turn({ kind: 'user', text: typed }), turn({ kind: 'found', source: 'typed', ids: result.added })]);
    setTyped('');
    setPhase('done');
  };

  const addDocument = (kind: DocumentKind, from: ClaimState = claim) => {
    const doc = scenario.documents[kind];
    setClaim(from);
    setTurns((t) => [...t, turn({ kind: 'attachment', name: doc?.file ?? 'Document' })]);
    setReading(true);
    setTimeout(
      () => {
        const result = readDocument(from, kind);
        setClaim(result.state);
        setAdded((a) => [...a, ...result.added]);
        setReading(false);
        setTurns((t) => [...t, turn({ kind: 'found', source: kind, ids: result.added })]);
        setPhase('done');
      },
      reduceMotion ? 0 : 1600,
    );
  };

  const removeProposal = (id: string) =>
    setClaim((c) => {
      const facts = { ...c.facts };
      delete facts[id];
      return { ...c, facts };
    });

  // ---- answer cards ----
  /** After a care area's details, ask once about good and bad days. */
  const followUps = (ask: Ask, facts: Facts): Ask[] => {
    const variability = VARIABILITY_ASK[ask.area];
    if (!variability || ask.id === variability || ask.kind === 'amount') return [];
    const gateYes = ask.gate && text(facts, ask.gate) === 'Yes';
    const open = statusOf(entry(ASK[variability].entries[0]), facts) === 'open';
    const queued = queue.some((q) => q.id === variability);
    return gateYes && open && !queued ? [ASK[variability]] : [];
  };

  const advance = (facts: Facts, rest: Ask[], newlyAdded: string[]) => {
    const next = { ...claim, facts };
    setClaim(next);
    setAdded((a) => [...a, ...newlyAdded]);
    if (rest.length) {
      setQueue(rest);
      setTurns((t) => [...t, turn({ kind: 'keni', text: say(rest[0].prompt, v) })]);
    } else {
      setQueue([]);
      setTurns((t) => [...t, turn({ kind: 'keni', text: ROUND.added })]);
      setPhase('done');
    }
  };

  const submitAsk = (values: Record<string, Value>, reply: string) => {
    const facts = answerAll(claim.facts, values);
    setTurns((t) => [...t, turn({ kind: 'user', text: reply })]);
    advance(facts, [...queue.slice(1), ...followUps(current, facts)], Object.keys(values));
  };

  const skipAsk = () => {
    const facts = current.entries.reduce(
      (f, id) => (statusOf(entry(id), f) === 'open' ? skip(f, id) : f),
      claim.facts,
    );
    setTurns((t) => [...t, turn({ kind: 'user', text: 'I’m not sure, we’ll talk it through' })]);
    advance(facts, queue.slice(1), []);
  };

  const submitBatch = (values: Record<string, Value>, reply: string) => {
    let facts = claim.facts;
    const ticked: Ask[] = [];
    for (const [id, value] of Object.entries(values)) {
      const no = value === 'No' || (Array.isArray(value) && value[0] === 'No');
      facts = answer(facts, id, value, no ? 'batch_check' : 'family');
      if (!no && OWNER[id]) ticked.push(OWNER[id]);
    }
    setClaim({ ...claim, facts, rounds: { ...claim.rounds, batchDone: true } });
    setTurns((t) => [...t, turn({ kind: 'user', text: reply })]);
    if (ticked.length) {
      setQueue(ticked);
      setPhase('ask');
      setTurns((t) => [...t, turn({ kind: 'keni', text: say(ticked[0].prompt, v) })]);
    } else {
      setTurns((t) => [...t, turn({ kind: 'keni', text: ROUND.added })]);
      setPhase('done');
    }
    setAdded((a) => [...a, ...Object.keys(values)]);
  };

  /** The first answer in an ask that two sources disagree about. */
  const conflictIn = (ask: Ask) => ask.entries.find((id) => claim.facts[id]?.state === 'conflict');

  /** "Say it instead": record against the area's own voice prompt. */
  const areaPrompt = current
    ? VOICE_PROMPTS.find((p) => p.area === current.area && scenario.voice[p.id])
    : undefined;

  // ---- render ----
  const renderTurn = (t: Turn) => {
    switch (t.kind) {
      case 'keni':
        return <ChatBubble from='keni' text={t.text} />;
      case 'user':
        return <ChatBubble from='user' text={t.text} />;
      case 'voice':
        return (
          <Stack sx={{ alignSelf: 'flex-end', maxWidth: '82%' }} spacing={0.75}>
            <VoiceNote seconds={t.seconds} tone='sent' />
            <Typography sx={{ px: 0.5 }} variant='body2'>
              “{t.words}”
            </Typography>
          </Stack>
        );
      case 'attachment':
        return <Attachment kind='document' name={t.name} state={reading ? 'processing' : 'sent'} />;
      case 'have':
        return <HaveCard ids={t.ids} facts={claim.facts} claim={claim} />;
      case 'found':
        return (
          <FoundCard
            source={t.source}
            ids={t.ids}
            facts={claim.facts}
            claim={claim}
            onRemove={phase === 'done' ? removeProposal : undefined}
          />
        );
    }
  };

  const heard = script
    ? heardSoFar(script, recording.kind === 'recording' ? recording.elapsed : playbackMs(script))
    : { text: '', lit: new Set<AreaId>(), seconds: 0 };
  const canSpeak = claim.aiConsent !== 'no' && Boolean(script);

  const composer = (withPhoto: boolean) => (
    <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1}>
      <TextField
        size='small'
        value={typed}
        placeholder={ROUND.typePlaceholder}
        onChange={(e) => setTyped(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') withConsent((from) => sendTyped(from));
        }}
        slotProps={{
          htmlInput: { 'aria-label': ROUND.typePlaceholder },
          input: withPhoto
            ? {
                startAdornment: (
                  <InputAdornment position='start'>
                    <IconButton
                      aria-label={ROUND.photo}
                      size='small'
                      onClick={() => withConsent((from) => addDocument('prescription', from))}
                    >
                      <Icon icon={'camera' as never} fontSize={16} />
                    </IconButton>
                  </InputAdornment>
                ),
              }
            : undefined,
        }}
        sx={{
          flex: 1,
          minWidth: 0,
          '& .MuiOutlinedInput-root': { borderRadius: `${radius.xxl}px`, pl: withPhoto ? 1 : 2 },
        }}
      />
      <IconButton aria-label={ROUND.sendTyped} disabled={!typed.trim()} onClick={() => withConsent((from) => sendTyped(from))} sx={sendButton}>
        <Icon icon={'paper-plane-top' as never} fontSize={16} />
      </IconButton>
    </Stack>
  );

  const capture =
    recording.kind === 'recording' && script ? (
      <Stack sx={{ alignItems: 'center' }} spacing={2}>
        <KeniOrb size={72} state='listening' />
        <Box
          aria-live='polite'
          sx={{
            width: '100%',
            maxHeight: 104,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            maskImage: 'linear-gradient(to bottom, transparent 0, black 36px)',
          }}
        >
          <Typography sx={{ textAlign: 'center' }} variant='body1'>
            {heard.text || ROUND.listening}
          </Typography>
        </Box>
        <Stack sx={{ flexWrap: 'wrap', justifyContent: 'center', gap: 0.75 }} direction='row' aria-label='Parts of the claim covered so far'>
          {AREAS.filter((a) => a.id !== 'start' && a.id !== 'stronger' && script.lines.some((l) => l.lights.includes(a.id))).map((a) => {
            const lit = heard.lit.has(a.id);
            return (
              <Chip
                key={a.id}
                size='small'
                label={say(a.label, v)}
                variant={lit ? 'filled' : 'outlined'}
                icon={lit ? <Icon icon={'circle-check' as never} fontSize={12} /> : undefined}
                sx={lit ? { bgcolor: 'custom.limeGreen.200', color: 'primary.main', '& .MuiChip-icon': { color: 'primary.main' } } : undefined}
              />
            );
          })}
        </Stack>
        <Stack sx={{ alignItems: 'center', width: '100%' }} direction='row' spacing={1}>
          <Box sx={{ flex: 1 }}>
            <VoiceNote seconds={heard.seconds} tone='recording' />
          </Box>
          <IconButton aria-label={ROUND.stop} onClick={() => setRecording({ kind: 'draft' })} sx={sendButton}>
            <Icon icon={'square' as never} fontSize={14} />
          </IconButton>
        </Stack>
      </Stack>
    ) : recording.kind === 'draft' ? (
      <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1}>
        <IconButton aria-label={ROUND.delete} onClick={() => setRecording({ kind: 'idle' })} sx={{ color: 'error.main' }}>
          <Icon icon={'trash' as never} fontSize={16} />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <VoiceNote seconds={script?.seconds ?? 10} tone='draft' />
        </Box>
        <IconButton aria-label={ROUND.send} onClick={sendVoice} sx={sendButton}>
          <Icon icon={'paper-plane-top' as never} fontSize={16} />
        </IconButton>
      </Stack>
    ) : (
      <Stack spacing={2}>
        {canSpeak && (
          <Stack sx={{ alignItems: 'center' }} spacing={1}>
            <Box
              component='button'
              type='button'
              aria-label={ROUND.orbLabel}
              onClick={() =>
                withConsent((from) => {
                  setClaim(from);
                  setRecording({ kind: 'recording', elapsed: 0 });
                })
              }
              sx={{
                p: 0,
                border: 0,
                bgcolor: 'transparent',
                cursor: 'pointer',
                borderRadius: '50%',
                '&:focus-visible': { outline: '3px solid', outlineColor: 'primary.main', outlineOffset: 3 },
              }}
            >
              <KeniOrb size={step.kind === 'opening' ? 80 : 60} state='idle' />
            </Box>
            <Typography variant='caption'>{ROUND.orbLabel}</Typography>
          </Stack>
        )}
        {claim.aiConsent !== 'no' && composer(step.kind === 'opening')}
      </Stack>
    );

  const footer =
    phase === 'done' ? (
      <MuiButton variant='contained' size='large' fullWidth onClick={() => finish(claim, [])}>
        {ROUND.done}
      </MuiButton>
    ) : phase === 'capture' ? (
      capture
    ) : null;

  return (
    <ScreenRoot relative>
      <StatusBar />
      <ClaimBar
        subtitle={step.kind === 'ask' ? say(step.ask.title, v) : undefined}
        onBack={onBack}
      />
      <Box
        ref={scroller}
        component='main'
        aria-label='Conversation'
        tabIndex={0}
        sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: SCREEN_PX, py: 3, '&:focus-visible': { outline: 'none' } }}
      >
        <Stack ref={content} spacing={1.5} role='log'>
          {turns.map((t) => (
            <Box key={t.id} sx={{ display: 'flex', flexDirection: 'column' }}>
              {renderTurn(t)}
            </Box>
          ))}
          {reading && <ChatTyping />}

          {phase === 'document' && step.kind === 'document' && (
            <Stack spacing={1}>
              <ClaimActionRow icon='camera' label={ROUND.photo} onClick={() => withConsent((from) => addDocument(step.doc, from))} />
              <ClaimActionRow icon='upload' label={ROUND.upload} onClick={() => withConsent((from) => addDocument(step.doc, from))} />
              <ClaimActionRow
                tone='go'
                label={ROUND.noDocument}
                onClick={() =>
                  onDone({ ...claim, rounds: { ...claim.rounds, documentsDone: [...claim.rounds.documentsDone, step.doc] } })
                }
              />
            </Stack>
          )}

          {phase === 'batch' && (
            <BatchCheckCard facts={claim.facts} voice={v} onSubmit={submitBatch} />
          )}

          {phase === 'ask' && current && conflictIn(current) && (
            <ConflictChooser
              id={conflictIn(current) as string}
              facts={claim.facts}
              claim={claim}
              onPick={(keep) =>
                setClaim((c) => ({ ...c, facts: settle(c.facts, conflictIn(current) as string, keep) }))
              }
            />
          )}

          {phase === 'ask' && current && !conflictIn(current) && (
            <Stack spacing={1}>
              <AskCard key={current.id} ask={current} facts={claim.facts} voice={v} onSubmit={submitAsk} onSkip={skipAsk} />
              {areaPrompt && claim.aiConsent !== 'no' && !claim.rounds.voiceDone.includes(areaPrompt.id) && (
                <ClaimActionRow
                  icon='microphone'
                  label={ROUND.sayInstead}
                  onClick={() => {
                    setPromptId(areaPrompt.id);
                    setTurns((t) => [...t, turn({ kind: 'keni', text: say(areaPrompt.prompt, v) })]);
                    setPhase('capture');
                  }}
                />
              )}
            </Stack>
          )}
        </Stack>
      </Box>

      {footer && (
        <Box sx={{ px: SCREEN_PX, pt: 2, pb: 1, bgcolor: 'background.default', borderTop: 1, borderColor: 'custom.neutral.200' }}>
          {footer}
        </Box>
      )}
      <ScreenFloor />

      {pending && (
        <BottomSheet
          variant='hug'
          title={ROUND.consentTitle}
          onClose={() => setPending(null)}
          footer={(close) => (
            <Stack spacing={2}>
              <MuiButton
                variant='contained'
                size='large'
                fullWidth
                onClick={() => {
                  close();
                  const run = pending;
                  setPending(null);
                  const consented: ClaimState = { ...claim, aiConsent: 'yes' };
                  setClaim(consented);
                  run(consented);
                }}
              >
                {ROUND.consentAccept}
              </MuiButton>
              <MuiButton
                variant='outlined'
                size='large'
                fullWidth
                onClick={() => {
                  close();
                  setPending(null);
                  // Declining reading by AI ends the voice round: the hub
                  // carries on with questions only.
                  onDone({
                    ...claim,
                    aiConsent: 'no',
                    rounds: { ...claim.rounds, openingDone: true },
                  });
                }}
              >
                {ROUND.consentDecline}
              </MuiButton>
            </Stack>
          )}
        >
          <Typography variant='body1'>{ROUND.consentBody}</Typography>
        </BottomSheet>
      )}
    </ScreenRoot>
  );
};

export default RoundScreen;
